import { useState, useEffect } from "react";
import { TagPill } from "./TagPill";
import type { CaptureFull } from "../lib/api-types";
import type { Vault } from "../contexts/vault-types";

interface Props {
  vault: Vault;
  slug: string;
  onClose: () => void;
}

export function CaptureDrawer({ vault, slug, onClose }: Props) {
  const [data, setData] = useState<CaptureFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    fetch(`/api/captures/${vault}/${slug}`)
      .then(async r => {
        if (cancelled) return;
        if (!r.ok) {
          setError(r.status === 404 ? "Capture not found — it may have been moved or deleted." : `Couldn't load capture (${r.status})`);
          return;
        }
        const d = (await r.json()) as CaptureFull;
        if (cancelled) return;
        setData(d);
        setTags(d.tags);
      })
      .catch(() => { if (!cancelled) setError("Network error — try again."); });
    fetch(`/api/tags?vault=${vault}`)
      .then(r => r.ok ? (r.json() as Promise<{ tag: string; count: number }[]>) : [])
      .then(all => { if (!cancelled) setSuggestions(all.map(t => t.tag)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [vault, slug]);

  if (error) {
    return (
      <div
        className="fixed inset-x-0 bottom-14 z-40 bg-attic-surface border-t border-attic-border rounded-t-2xl p-4 shadow-2xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="w-8 h-1 bg-attic-border rounded mx-auto mb-3 cursor-pointer" onClick={onClose} />
        <p className="text-center text-red-600 text-sm">{error}</p>
        <button onClick={onClose} className="mx-auto block mt-3 text-attic-accent text-xs font-semibold">Close</button>
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="fixed inset-x-0 bottom-14 z-40 bg-attic-surface border-t border-attic-border rounded-t-2xl p-4 text-attic-dim text-center text-sm"
        role="dialog"
        aria-modal="true"
      >
        <div className="w-8 h-1 bg-attic-border rounded mx-auto mb-3 cursor-pointer" onClick={onClose} />
        Loading…
      </div>
    );
  }

  const saveTags = async (next: string[]) => {
    await fetch(`/api/captures/${vault}/${slug}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tags: next }),
    });
    setTags(next);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-14 z-40 bg-attic-surface border-t border-attic-border rounded-t-2xl p-4 max-h-[85%] overflow-y-auto shadow-2xl"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-semibold text-sm flex-1 pr-3">{data.title}</h3>
        <button
          onClick={onClose}
          aria-label="Close"
          className="-mt-1 -mr-1 w-8 h-8 flex items-center justify-center rounded-full text-attic-dim hover:bg-attic-bg text-xl leading-none"
        >
          ×
        </button>
      </div>
      <div className="w-12 h-1 bg-attic-border rounded mx-auto -mt-1 mb-3 cursor-pointer" onClick={onClose} aria-label="Close drawer" />
      <p className="text-[10px] text-attic-dim mb-3">
        {data.source} · captured {data.capturedAt.slice(0, 10)}
      </p>

      <p className="text-[9px] uppercase font-bold text-attic-dim tracking-wider mt-3">AI Summary</p>
      <p className="text-xs mt-1 leading-relaxed">{data.summary}</p>

      <p className="text-[9px] uppercase font-bold text-attic-dim tracking-wider mt-3">Original</p>
      {data.mediaUrl && <video src={data.mediaUrl} controls className="w-full rounded mt-2" />}
      <div className="text-xs mt-1 font-serif leading-relaxed whitespace-pre-wrap">{data.originalText}</div>

      <div className="flex items-center justify-between mt-3">
        <p className="text-[9px] uppercase font-bold text-attic-dim tracking-wider">Tags</p>
        <button
          onClick={() => setEditing(e => !e)}
          className="text-[10px] text-attic-accent font-semibold"
          aria-label={editing ? "Finish editing tags" : "Edit tags"}
        >
          {editing ? "done" : "edit"}
        </button>
      </div>
      <div className="flex flex-wrap gap-1 mt-1">
        {tags.map(t => (
          <TagPill
            key={t}
            vault={vault}
            tag={t}
            editable={editing}
            onRemove={() => saveTags(tags.filter(x => x !== t))}
          />
        ))}
      </div>
      {editing && (
        <>
          <input
            placeholder="+ add tag and press Enter"
            className="text-xs mt-2 px-2 py-1 border border-dashed border-attic-border rounded-full bg-attic-bg w-full"
            onKeyDown={e => {
              if (e.key === "Enter") {
                const input = e.target as HTMLInputElement;
                const value = input.value.trim();
                if (value) {
                  const next = Array.from(new Set([...tags, value]));
                  saveTags(next);
                  input.value = "";
                }
              }
            }}
          />
          <p className="text-[9px] uppercase font-bold text-attic-dim tracking-wider mt-3">Suggestions</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {suggestions.filter(s => !tags.includes(s)).slice(0, 8).map(s => (
              <button
                key={s}
                onClick={() => saveTags([...tags, s])}
                className="text-[10px] border border-attic-border rounded-full px-2 py-0.5"
              >
                + {s}
              </button>
            ))}
          </div>
        </>
      )}

      {data.url && (
        <div className="mt-4 pt-3 border-t border-attic-border text-center">
          <a
            href={data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-attic-accent text-[11px] underline"
          >
            View original on {data.source} ↗
          </a>
        </div>
      )}
    </div>
  );
}
